import {
  aiCopyGroupEditResponseSchema,
  aiCopyItemSchema,
  aiCopyManufacturersSchema,
  aiCopyQueuedResponseSchema,
  aiCopySummarySchema,
  approveAiCopyResponseSchema,
  auditEntrySchema,
  catalogIngestRequestedSchema,
  catalogIngestRunSchema,
  catalogSummarySchema,
  createTenantResponseSchema,
  domainDetailSchema,
  domainListResponseSchema,
  issueCertificateResponseSchema,
  excludedProductListSchema,
  invitePlatformStaffResponseSchema,
  issuedInvitationSchema,
  masterCategoryListSchema,
  masterProductIndexSchema,
  masterProductSchema,
  pageResponseSchema,
  platformAiOverviewSchema,
  platformDomainsResponseSchema,
  platformServiceTicketsSchema,
  platformStaffMemberSchema,
  productCategoryGroupsSchema,
  productDetailSchema,
  productFlyerSchema,
  provisioningRetryResponseSchema,
  storeContactSchema,
  storeDesignViewSchema,
  storeEntrySchema,
  storeMailSchema,
  serviceTicketDetailSchema,
  serviceTicketListSchema,
  tenantAiStatusSchema,
  tenantDetailSchema,
  tenantMediaSchema,
  tenantProvisioningSchema,
  tenantSummarySchema,
  updateExcludedProductsResponseSchema,
  type AddDomainRequest,
  type AiCopyFilter,
  type AiCopyGroupEditRequest,
  type ApproveAiCopyRequest,
  type CatalogIngestMode,
  type ConnectStoreMailRequest,
  type CreateTenantRequest,
  type DomainStatus,
  type InvitePlatformStaffRequest,
  type PlatformProductUpdateRequest,
  type PlatformRole,
  type RegenerateAiCopyRequest,
  type ReplaceProductMediaRequest,
  type RestoreProductListRequest,
  type ServiceTicketStatus,
  type SetProductLogoRequest,
  type SetTenantAiBudgetRequest,
  type TenantLifecycleAction,
  type UpdateAiCopySettingsRequest,
  type UpdateExcludedProductsRequest,
  type UpdateStoreDesignRequest,
  type UpdateStoreWhatsappRequest,
} from '@avrahami/contracts';
import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { aiPollInterval, provisioningPollInterval } from '../lib/ai';
import { AI_COPY_POLL_MS, copyWorkOpen } from '../lib/ai-copy';
import { api } from '../lib/api';
import { domainInProgress, DOMAINS_POLL_MS, needsPolling } from '../lib/domains';

const PAGE_SIZE = 25;
/** Lists start without a cursor. */
const FIRST_PAGE: string | undefined = undefined;

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const text = query.toString();
  return text === '' ? path : `${path}?${text}`;
}

const tenantPage = pageResponseSchema(tenantSummarySchema);
const auditPage = pageResponseSchema(auditEntrySchema);
const staffList = z.object({ items: z.array(platformStaffMemberSchema) });
const ingestRunPage = pageResponseSchema(catalogIngestRunSchema);
const aiCopyPage = pageResponseSchema(aiCopyItemSchema);
const masterProductPage = pageResponseSchema(masterProductSchema);

/** The routes of one store's marketing copy. */
function copyPath(tenantId: string, rest = ''): string {
  return `/v1/platform/tenants/${tenantId}/ai-copy${rest}`;
}

/** The routes of one catalog product's page in one store (adr/0043). */
function productPath(tenantId: string, productId: string, rest = ''): string {
  return `/v1/platform/tenants/${tenantId}/products/${encodeURIComponent(productId)}${rest}`;
}

/** Query keys in one place, so mutations invalidate exactly what they change. */
export const keys = {
  tenants: ['tenants'] as const,
  tenantList: (slug: string) => ['tenants', 'list', slug] as const,
  tenant: (id: string) => ['tenants', 'detail', id] as const,
  provisioning: (id: string) => ['tenants', 'provisioning', id] as const,
  tenantAi: (id: string) => ['tenants', 'ai', id] as const,
  tenantCopy: (id: string) => ['tenants', 'copy', id] as const,
  tenantCopySummary: (id: string) => ['tenants', 'copy', id, 'summary'] as const,
  tenantCopyItems: (id: string) => ['tenants', 'copy', id, 'items'] as const,
  tenantFlyer: (id: string, productId: string) =>
    ['tenants', 'copy', id, 'flyer', productId] as const,
  tenantProduct: (id: string, productId: string) =>
    ['tenants', 'copy', id, 'product', productId] as const,
  exclusions: (id: string) => ['tenants', 'exclusions', id] as const,
  tenantDomains: (id: string) => ['tenants', 'domains', id] as const,
  tenantDomainList: (id: string) => ['tenants', 'domains', id, 'list'] as const,
  tenantDomain: (id: string, domainId: string) =>
    ['tenants', 'domains', id, 'detail', domainId] as const,
  tenantContact: (id: string) => ['tenants', 'contact', id] as const,
  tenantDesign: (id: string) => ['tenants', 'design', id] as const,
  tenantService: (id: string) => ['tenants', 'service', id] as const,
  tenantServiceList: (id: string) => ['tenants', 'service', id, 'list'] as const,
  tenantServiceTicket: (id: string, ticketId: string) =>
    ['tenants', 'service', id, 'ticket', ticketId] as const,
  serviceEverywhere: ['service-tickets'] as const,
  audit: (tenantId: string) => ['audit', tenantId] as const,
  staff: ['staff'] as const,
  catalog: ['catalog'] as const,
  ai: ['ai'] as const,
  domains: (status: DomainStatus | undefined) => ['domains', status ?? 'all'] as const,
};

export function tenantListQuery(slug: string) {
  return infiniteQueryOptions({
    queryKey: keys.tenantList(slug),
    queryFn: ({ pageParam }) =>
      api.request(
        'GET',
        withQuery('/v1/platform/tenants', {
          limit: String(PAGE_SIZE),
          cursor: pageParam,
          slug: slug === '' ? undefined : slug,
        }),
        tenantPage,
      ),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** Every tenant (up to 100), for pickers and for naming tenants in the audit log. */
export const allTenantsQuery = queryOptions({
  queryKey: ['tenants', 'all'],
  queryFn: () => api.request('GET', '/v1/platform/tenants?limit=100', tenantPage),
  staleTime: 60_000,
});

export function tenantQuery(id: string) {
  return queryOptions({
    queryKey: keys.tenant(id),
    queryFn: () => api.request('GET', `/v1/platform/tenants/${id}`, tenantDetailSchema),
  });
}

/**
 * Polls while the worker is on it, and lightly while the store's copy is written (the run then
 * continues by itself); a finished or failed run changes only by action.
 */
export function provisioningQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.provisioning(tenantId),
    queryFn: () =>
      api.request('GET', `/v1/platform/tenants/${tenantId}/provisioning`, tenantProvisioningSchema),
    refetchInterval: (query) => provisioningPollInterval(query.state.data?.run),
  });
}

/** One store's content state and budget. Polls while the store has open content jobs. */
export function tenantAiQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.tenantAi(tenantId),
    queryFn: () => api.request('GET', `/v1/platform/tenants/${tenantId}/ai`, tenantAiStatusSchema),
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs;
      return aiPollInterval(jobs === undefined ? undefined : [jobs]);
    },
  });
}

/** The state of one store's copy: counts by state, open jobs, and the approval setting. */
export function tenantCopySummaryQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.tenantCopySummary(tenantId),
    queryFn: () => api.request('GET', copyPath(tenantId), aiCopySummarySchema),
    // Writing in progress is followed until it ends; otherwise nothing is polled.
    refetchInterval: (query) => {
      const summary = query.state.data;
      return summary !== undefined && copyWorkOpen(summary) ? AI_COPY_POLL_MS : false;
    },
  });
}

/** What one product's flyer is drawn from (docs/FLYERS.md). */
export function tenantFlyerQuery(tenantId: string, productId: string) {
  return queryOptions({
    queryKey: keys.tenantFlyer(tenantId, productId),
    queryFn: () =>
      api.request(
        'GET',
        copyPath(tenantId, `/items/${encodeURIComponent(productId)}/flyer`),
        productFlyerSchema,
      ),
  });
}

/** One catalog product of the store, as its page shows it (adr/0043). */
export function tenantProductQuery(tenantId: string, productId: string) {
  return queryOptions({
    queryKey: keys.tenantProduct(tenantId, productId),
    queryFn: () => api.request('GET', productPath(tenantId, productId), productDetailSchema),
  });
}

/** The manufacturers of the store's catalog products with copy, for a group edit. */
export function tenantManufacturersQuery(tenantId: string) {
  return queryOptions({
    queryKey: [...keys.tenantCopy(tenantId), 'manufacturers'],
    queryFn: () =>
      api.request('GET', copyPath(tenantId, '/manufacturers'), aiCopyManufacturersSchema),
  });
}

export interface AiCopyFilters {
  filter: AiCopyFilter;
  q: string;
}

/**
 * The store's catalog products in one state of their copy: the ones of one category
 * (`categoryId`), or all of them.
 */
export function tenantCopyItemsQuery(tenantId: string, filters: AiCopyFilters, categoryId = '') {
  const q = filters.q.trim();
  return infiniteQueryOptions({
    queryKey: [...keys.tenantCopyItems(tenantId), filters.filter, q, categoryId],
    queryFn: ({ pageParam }) =>
      api.request(
        'GET',
        withQuery(copyPath(tenantId, '/items'), {
          limit: String(PAGE_SIZE),
          cursor: pageParam,
          filter: filters.filter,
          q,
          categoryId,
        }),
        aiCopyPage,
      ),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** The store's categories, with the products in one state of their copy counted in each. */
export function tenantCopyGroupsQuery(tenantId: string, filter: AiCopyFilter) {
  return queryOptions({
    queryKey: [...keys.tenantCopy(tenantId), 'groups', filter],
    queryFn: () =>
      api.request(
        'GET',
        withQuery(copyPath(tenantId, '/categories'), { filter }),
        productCategoryGroupsSchema,
      ),
    // The categories stay (and stay open) while another state of the copy is counted.
    placeholderData: keepPreviousData,
  });
}

/** The catalog products the platform keeps out of one store. */
export function excludedProductsQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.exclusions(tenantId),
    queryFn: () =>
      api.request(
        'GET',
        `/v1/platform/tenants/${tenantId}/excluded-products`,
        excludedProductListSchema,
      ),
  });
}

export interface MasterProductFilters {
  q: string;
  /** Empty for every category; `none` for the products placed in no category. */
  categoryId: string;
}

/** The master catalog's products (facts only), for pickers. */
export function masterProductsQuery(filters: MasterProductFilters) {
  const q = filters.q.trim();
  return infiniteQueryOptions({
    queryKey: [...keys.catalog, 'products', q, filters.categoryId],
    queryFn: ({ pageParam }) =>
      api.request(
        'GET',
        withQuery('/v1/platform/catalog/products', {
          limit: String(PAGE_SIZE),
          cursor: pageParam,
          q,
          categoryId: filters.categoryId,
        }),
        masterProductPage,
      ),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
  });
}

/** The catalog's categories, with the active products placed directly in each counted. */
export const masterCategoriesQuery = queryOptions({
  queryKey: [...keys.catalog, 'categories'],
  queryFn: () => api.request('GET', '/v1/platform/catalog/categories', masterCategoryListSchema),
  staleTime: 60_000,
});

/** Every active product of the catalog, short: for ticking whole categories and views at once. */
export const masterProductIndexQuery = queryOptions({
  queryKey: [...keys.catalog, 'product-index'],
  queryFn: () => api.request('GET', '/v1/platform/catalog/product-index', masterProductIndexSchema),
  staleTime: 60_000,
});

/** This month's content spend of every store. Polls while any store has open content jobs. */
export const platformAiQuery = queryOptions({
  queryKey: keys.ai,
  queryFn: () => api.request('GET', '/v1/platform/ai', platformAiOverviewSchema),
  refetchInterval: (query) =>
    aiPollInterval(query.state.data?.tenants.map((tenant) => tenant.jobs)),
});

/** Every store's own domains, in one status or in all of them, and the counts per status. */
export function platformDomainsQuery(status: DomainStatus | undefined) {
  return queryOptions({
    queryKey: keys.domains(status),
    queryFn: () =>
      api.request(
        'GET',
        withQuery('/v1/platform/domains', { status }),
        platformDomainsResponseSchema,
      ),
    // The worker keeps checking in the background: the list follows it lightly.
    refetchInterval: DOMAINS_POLL_MS,
  });
}

function tenantDomainsPath(tenantId: string, domainId?: string): string {
  const base = `/v1/platform/tenants/${encodeURIComponent(tenantId)}/domains`;
  return domainId === undefined ? base : `${base}/${encodeURIComponent(domainId)}`;
}

/** How often a store's addresses that are still being checked are read again. */
export const STORE_DOMAIN_POLL_MS = 10_000;

/** One store's addresses: its system address and its own domains. */
export function tenantDomainsQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.tenantDomainList(tenantId),
    queryFn: () => api.request('GET', tenantDomainsPath(tenantId), domainListResponseSchema),
    // Checks in progress are followed until they end; otherwise nothing is polled.
    refetchInterval: (query) =>
      needsPolling(query.state.data?.items) ? STORE_DOMAIN_POLL_MS : false,
  });
}

/** One address with the DNS records it needs and what the last check saw. */
export function tenantDomainQuery(tenantId: string, domainId: string) {
  return queryOptions({
    queryKey: keys.tenantDomain(tenantId, domainId),
    queryFn: () => api.request('GET', tenantDomainsPath(tenantId, domainId), domainDetailSchema),
    refetchInterval: (query) => {
      const detail = query.state.data;
      return detail !== undefined && domainInProgress(detail.domain) ? STORE_DOMAIN_POLL_MS : false;
    },
  });
}

function tenantContactPath(tenantId: string, rest = ''): string {
  return `/v1/platform/tenants/${encodeURIComponent(tenantId)}/contact${rest}`;
}

/** One store's WhatsApp number and connected Gmail account (adr/0028). */
export function tenantContactQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.tenantContact(tenantId),
    queryFn: () => api.request('GET', tenantContactPath(tenantId), storeContactSchema),
  });
}

/** How often the screens of faults and calls are read again: a new call shows up by itself. */
export const SERVICE_POLL_MS = 60_000;

function tenantServicePath(tenantId: string, rest = ''): string {
  return `/v1/platform/tenants/${encodeURIComponent(tenantId)}/service-tickets${rest}`;
}

/** One store's faults (from the scan) and its service calls (adr/0036). */
export function tenantServiceQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.tenantServiceList(tenantId),
    queryFn: () => api.request('GET', tenantServicePath(tenantId), serviceTicketListSchema),
    refetchInterval: SERVICE_POLL_MS,
  });
}

/** One ticket with its messages. */
export function tenantServiceTicketQuery(tenantId: string, ticketId: string) {
  return queryOptions({
    queryKey: keys.tenantServiceTicket(tenantId, ticketId),
    queryFn: () =>
      api.request(
        'GET',
        tenantServicePath(tenantId, `/${encodeURIComponent(ticketId)}`),
        serviceTicketDetailSchema,
      ),
  });
}

/** The tickets still open in every store. */
export const serviceEverywhereQuery = queryOptions({
  queryKey: keys.serviceEverywhere,
  queryFn: () => api.request('GET', '/v1/platform/service-tickets', platformServiceTicketsSchema),
  refetchInterval: SERVICE_POLL_MS,
});

function tenantDesignPath(tenantId: string, rest = ''): string {
  return `/v1/platform/tenants/${encodeURIComponent(tenantId)}/design${rest}`;
}

/** How often a store's design is read again while the AI writes its opening banner. */
export const STORE_HERO_POLL_MS = 4_000;

/** One store's design and opening banner (adr/0034); followed while the banner is written. */
export function tenantDesignQuery(tenantId: string) {
  return queryOptions({
    queryKey: keys.tenantDesign(tenantId),
    queryFn: () => api.request('GET', tenantDesignPath(tenantId), storeDesignViewSchema),
    refetchInterval: (query) => {
      const state = query.state.data?.heroJob.state;
      return state === 'QUEUED' || state === 'RUNNING' ? STORE_HERO_POLL_MS : false;
    },
  });
}

export function auditQuery(tenantId: string) {
  return infiniteQueryOptions({
    queryKey: keys.audit(tenantId),
    queryFn: ({ pageParam }) =>
      api.request(
        'GET',
        withQuery('/v1/platform/audit-log', {
          limit: String(PAGE_SIZE),
          cursor: pageParam,
          tenantId: tenantId === '' ? undefined : tenantId,
        }),
        auditPage,
      ),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export const staffQuery = queryOptions({
  queryKey: keys.staff,
  queryFn: () => api.request('GET', '/v1/platform/staff', staffList),
});

export const catalogSummaryQuery = queryOptions({
  queryKey: [...keys.catalog, 'summary'],
  queryFn: () => api.request('GET', '/v1/platform/catalog/summary', catalogSummarySchema),
  refetchInterval: 30_000,
});

export function ingestRunsQuery() {
  return infiniteQueryOptions({
    queryKey: [...keys.catalog, 'runs'],
    queryFn: ({ pageParam }) =>
      api.request(
        'GET',
        withQuery('/v1/platform/catalog/ingest-runs', {
          limit: String(PAGE_SIZE),
          cursor: pageParam,
        }),
        ingestRunPage,
      ),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });
}

export const platformApi = {
  addDomain: (tenantId: string, body: AddDomainRequest) =>
    api.request('POST', tenantDomainsPath(tenantId), domainDetailSchema, body),
  // Checks the address now, and answers what the check saw.
  checkDomain: (tenantId: string, domainId: string) =>
    api.request('POST', `${tenantDomainsPath(tenantId, domainId)}/check`, domainDetailSchema),
  // Answers every address: the one that was the primary changed too.
  makeDomainPrimary: (tenantId: string, domainId: string) =>
    api.request(
      'POST',
      `${tenantDomainsPath(tenantId, domainId)}/primary`,
      domainListResponseSchema,
    ),
  /** Waits while the server makes the site and obtains the certificate: up to a few minutes. */
  issueDomainCertificate: (tenantId: string, domainId: string) =>
    api.request(
      'POST',
      `${tenantDomainsPath(tenantId, domainId)}/certificate`,
      issueCertificateResponseSchema,
    ),
  removeDomain: (tenantId: string, domainId: string) =>
    api.send('DELETE', tenantDomainsPath(tenantId, domainId)),

  // An entry token for the store's own admin, good for a minute (adr/0031).
  startStoreManagement: (tenantId: string) =>
    api.request(
      'POST',
      `/v1/platform/tenants/${encodeURIComponent(tenantId)}/management-session`,
      storeEntrySchema,
    ),

  setStoreWhatsapp: (tenantId: string, body: UpdateStoreWhatsappRequest) =>
    api.request('PUT', tenantContactPath(tenantId, '/whatsapp'), storeContactSchema, body),
  // Gmail is asked first: the account is kept only when Google takes it.
  connectStoreMail: (tenantId: string, body: ConnectStoreMailRequest) =>
    api.request('PUT', tenantContactPath(tenantId, '/mail'), storeMailSchema, body),
  disconnectStoreMail: (tenantId: string) =>
    api.request('DELETE', tenantContactPath(tenantId, '/mail'), storeMailSchema),
  testStoreMail: (tenantId: string) =>
    api.request('POST', tenantContactPath(tenantId, '/mail/test'), storeMailSchema),
  scanStore: (tenantId: string) =>
    api.request('POST', tenantServicePath(tenantId, '/scan'), serviceTicketListSchema),
  replyServiceTicket: (tenantId: string, ticketId: string, body: string) =>
    api.request(
      'POST',
      tenantServicePath(tenantId, `/${encodeURIComponent(ticketId)}/messages`),
      serviceTicketDetailSchema,
      { body },
    ),
  setServiceTicketStatus: (tenantId: string, ticketId: string, status: ServiceTicketStatus) =>
    api.request(
      'PUT',
      tenantServicePath(tenantId, `/${encodeURIComponent(ticketId)}/status`),
      serviceTicketDetailSchema,
      { status },
    ),
  updateStoreDesign: (tenantId: string, body: UpdateStoreDesignRequest) =>
    api.request('PUT', tenantDesignPath(tenantId), storeDesignViewSchema, body),
  /** The store's logo itself, for reading its colours in the browser. */
  storeLogo: (tenantId: string) => api.blob(tenantDesignPath(tenantId, '/logo')),
  regenerateStoreHero: (tenantId: string) =>
    api.request('POST', tenantDesignPath(tenantId, '/hero/regenerate'), aiCopyQueuedResponseSchema),

  requestCatalogIngest: (mode: CatalogIngestMode) =>
    api.request('POST', '/v1/platform/catalog/ingest', catalogIngestRequestedSchema, { mode }),
  createTenant: (body: CreateTenantRequest) =>
    api.request('POST', '/v1/platform/tenants', createTenantResponseSchema, body),
  changeLifecycle: (id: string, action: TenantLifecycleAction, reason: string) =>
    api.request(
      'POST',
      `/v1/platform/tenants/${id}/${action}`,
      tenantSummarySchema,
      reason.trim() === '' ? {} : { reason: reason.trim() },
    ),
  retryProvisioning: (tenantId: string) =>
    api.request(
      'POST',
      `/v1/platform/tenants/${tenantId}/provisioning/retry`,
      provisioningRetryResponseSchema,
    ),
  /** `null` returns the store to the platform's default limit. */
  setTenantAiBudget: (tenantId: string, monthlyLimitMicros: number | null) =>
    api.request('PUT', `/v1/platform/tenants/${tenantId}/ai-budget`, tenantAiStatusSchema, {
      monthlyLimitMicros,
    } satisfies SetTenantAiBudgetRequest),
  updateExcludedProducts: (tenantId: string, body: UpdateExcludedProductsRequest) =>
    api.request(
      'PUT',
      `/v1/platform/tenants/${tenantId}/excluded-products`,
      updateExcludedProductsResponseSchema,
      body,
    ),

  /** Sets the store's customer number (null removes it); answers the store's page again. */
  updateCustomerNumber: (tenantId: string, customerNumber: string) =>
    api.request('PUT', `/v1/platform/tenants/${tenantId}/customer-number`, tenantDetailSchema, {
      customerNumber,
    }),

  approveAiCopy: (tenantId: string, body: ApproveAiCopyRequest) =>
    api.request('POST', copyPath(tenantId, '/approve'), approveAiCopyResponseSchema, body),
  // The three below only queue work (202); the summary follows it.
  regenerateAiCopy: (tenantId: string, body: RegenerateAiCopyRequest) =>
    api.request('POST', copyPath(tenantId, '/regenerate'), aiCopyQueuedResponseSchema, body),
  retryFailedAiCopy: (tenantId: string) =>
    api.request('POST', copyPath(tenantId, '/retry-failed'), aiCopyQueuedResponseSchema),
  generateMissingAiCopy: (tenantId: string) =>
    api.request('POST', copyPath(tenantId, '/generate-missing'), aiCopyQueuedResponseSchema),
  regenerateStoreSeo: (tenantId: string) =>
    api.request('POST', copyPath(tenantId, '/store-seo/regenerate'), aiCopyQueuedResponseSchema),
  updateAiCopySettings: (tenantId: string, body: UpdateAiCopySettingsRequest) =>
    api.request('PUT', copyPath(tenantId, '/settings'), aiCopySummarySchema, body),
  /** One field of the copy of a whole group, edited (or, with `preview`, only counted). */
  groupEditCopy: (tenantId: string, body: AiCopyGroupEditRequest) =>
    api.request('POST', copyPath(tenantId, '/group-edit'), aiCopyGroupEditResponseSchema, body),

  // One catalog product's page (adr/0043); every save carries the product's version.
  updateTenantProduct: (tenantId: string, productId: string, body: PlatformProductUpdateRequest) =>
    api.request('PATCH', productPath(tenantId, productId), productDetailSchema, body),
  replaceTenantProductMedia: (
    tenantId: string,
    productId: string,
    body: ReplaceProductMediaRequest,
  ) => api.request('PUT', productPath(tenantId, productId, '/media'), productDetailSchema, body),
  // Gives a list the store edited back to the engine (adr/0055); answers the product.
  restoreTenantProductList: (
    tenantId: string,
    productId: string,
    body: RestoreProductListRequest,
  ) =>
    api.request(
      'POST',
      productPath(tenantId, productId, '/lists/restore'),
      productDetailSchema,
      body,
    ),
  setTenantProductLogo: (tenantId: string, productId: string, body: SetProductLogoRequest) =>
    api.request('PUT', productPath(tenantId, productId, '/logo'), productDetailSchema, body),
  /** An image of the store, uploaded by the staff for one of its product pages. */
  uploadTenantMedia: (tenantId: string, file: File) =>
    api.upload(`/v1/platform/tenants/${tenantId}/media`, file, tenantMediaSchema),

  inviteOwner: (tenantId: string, userId: string) =>
    api.request(
      'POST',
      `/v1/platform/tenants/${tenantId}/owners/${userId}/invitation`,
      issuedInvitationSchema,
    ),
  /** A new password for a store owner (adr/0035, 7); the owner's sessions end. */
  setOwnerPassword: (tenantId: string, userId: string, password: string) =>
    api.send('PUT', `/v1/platform/tenants/${tenantId}/owners/${userId}/password`, { password }),
  inviteStaff: (body: InvitePlatformStaffRequest) =>
    api.request('POST', '/v1/platform/staff', invitePlatformStaffResponseSchema, body),
  changeStaffRole: (userId: string, role: PlatformRole) =>
    api.request('PATCH', `/v1/platform/staff/${userId}`, platformStaffMemberSchema, { role }),
  removeStaff: (userId: string) => api.send('DELETE', `/v1/platform/staff/${userId}`),
  reissueStaffInvitation: (userId: string) =>
    api.request('POST', `/v1/platform/staff/${userId}/invitation`, issuedInvitationSchema),
};
